import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date-only string (YYYY-MM-DD) as local time.
 * This avoids timezone issues where `new Date('2026-01-14')` or `parseISO('2026-01-14')`
 * would be interpreted as UTC midnight, causing off-by-one day errors in local time.
 */
export function parseDateOnly(dateStr: string): Date {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a Date object to a date-only string (YYYY-MM-DD) in local time.
 * This ensures the date string represents the same calendar day the user sees.
 */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a time string (HH:mm or HH:mm:ss) to 12-hour format with AM/PM.
 * Handles various input formats including raw database time strings.
 */
export function formatTime12Hour(time: string | null | undefined): string {
  if (!time) return '';
  try {
    // Handle HH:mm:ss format
    const parts = time.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes} ${period}`;
    }
    return time;
  } catch {
    return time;
  }
}

/**
 * Format meeting days + daily times, e.g. "Tuesdays: 3:30 PM - 5:30 PM".
 * Returns '' when there is nothing meaningful to show.
 */
export function formatDaysAndTimes(
  days: string[] | null | undefined,
  start: string | null | undefined,
  end: string | null | undefined,
  allWeekdaysLabel = 'School Days'
): string {
  const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const ORDER = [...WEEKDAYS, 'saturday', 'sunday'];
  const clean = (days || []).filter(Boolean);
  const normalized = clean.map((d) => d.toLowerCase());
  const isAllWeekdays =
    normalized.length === 5 && WEEKDAYS.every((d) => normalized.includes(d));
  // When the caller passes a generic label like "Daily" (camps), always use it
  // regardless of how many days the program actually meets.
  const useGenericLabel = allWeekdaysLabel !== 'School Days';
  const dayLabel = useGenericLabel || isAllWeekdays
    ? allWeekdaysLabel
    : [...clean]
        .sort((a, b) => ORDER.indexOf(a.toLowerCase()) - ORDER.indexOf(b.toLowerCase()))
        .map((d) => (/s$/i.test(d) ? d : `${d}s`))
        .join(', ');
  const timeLabel = start && end ? `${formatTime12Hour(start)} - ${formatTime12Hour(end)}` : '';
  if (dayLabel && timeLabel) return `${dayLabel}: ${timeLabel}`;
  return dayLabel || timeLabel;
}
